/**
 * Composer.MentionAutocomplete — ``@``-trigger suggestion popover (LEGACY
 * textarea driver).
 *
 * Pattern mirrors ``SlashCommandMenu`` (slash palette): inline-rendered
 * above the textarea, headless (the parent owns the query/regex + fetch),
 * keyboard nav via a document listener so the textarea keeps focus.
 *
 * NOTE (May 2026 Tiptap migration): the panel **body** has been extracted
 * into ``MentionSuggestionList`` so the same markup is reused by the
 * Tiptap-driven path (``RichTextEditor``). This shell keeps the legacy
 * document-keydown contract so existing textarea consumers continue
 * working unchanged. New code SHOULD prefer ``Composer.RichEditor`` which
 * embeds mentions as first-class nodes (chips).
 *
 * Accessibility:
 *   - ``role="menu"`` + per-row ``role="menuitem"`` (matches the sibling
 *     ``SlashCommandMenu`` convention and keeps biome's
 *     ``useSemanticElements`` happy; we don't use ``listbox``/``option``
 *     because the textarea retains focus and the dropdown is operated via
 *     synthetic active-index — ``aria-current`` on the active item gives
 *     screen readers the same affordance without the focus-management
 *     contract that ``listbox`` implies)
 *   - Arrow Up/Down move the active row, Enter selects, Esc dismisses
 *   - Mouse hover updates the active index (no scroll-into-view jumps)
 *
 * Italian copy: "Nessun risultato", "Cerca persone, documenti, cartelle…".
 */

import * as React from "react";
import {
  MentionSuggestionList,
  type MentionSuggestionListHandle,
  type MentionAutocompleteIcon,
  type MentionAutocompleteKind,
  type MentionAutocompleteResult,
} from "./MentionSuggestionList";

// Re-export types so existing consumers (`@ando/ui` barrel) keep their
// import surface unchanged after the May 2026 split.
export type {
  MentionAutocompleteIcon,
  MentionAutocompleteKind,
  MentionAutocompleteResult,
};

export interface MentionAutocompleteProps {
  /** Whether the popover is currently rendered. */
  readonly open: boolean;
  /** Current ``@xxx`` query (sans the leading ``@``). Drives the empty-state copy. */
  readonly query: string;
  /** Pre-fetched, ranked suggestion list. */
  readonly results: readonly MentionAutocompleteResult[];
  /** Loading flag — surfaces a subtle "Cerco…" hint without flicker. */
  readonly loading?: boolean;
  /** Called when the user picks a row (click or Enter). */
  readonly onSelect: (mention: MentionAutocompleteResult) => void;
  /** Called when the user presses Escape or blurs out. */
  readonly onDismiss: () => void;
  /** Optional extra class for the wrapping panel. */
  readonly className?: string;
}

export const MentionAutocomplete = React.forwardRef<
  HTMLDivElement,
  MentionAutocompleteProps
>(({ open, query, results, loading, onSelect, onDismiss, className }, _ref) => {
  const handleRef = React.useRef<MentionSuggestionListHandle | null>(null);

  // Keyboard nav lives on a document listener (capture phase) so the
  // textarea keeps focus and doesn't have to forward arrow keys. We only
  // bind while open. We delegate the arrow/Enter behaviour to the panel's
  // imperative API to keep the DRY contract with the Tiptap driver.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onDismiss();
        return;
      }
      const handled = handleRef.current?.onKeyDown({ key: e.key }) ?? false;
      if (handled) {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () =>
      window.removeEventListener("keydown", onKey, { capture: true });
  }, [open, onDismiss]);

  if (!open) return null;

  return (
    <MentionSuggestionList
      ref={handleRef}
      query={query}
      results={results}
      loading={loading}
      onSelect={onSelect}
      className={className}
    />
  );
});
MentionAutocomplete.displayName = "Composer.MentionAutocomplete";
